// Agenda de citas. Centro Medico → Agenda → Agenda.
// Vistas día/semana/mes. Disponibilidad y validación de cupos en la BD (RPCs).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  Campo, Cita, ESTADO_CITA, MedicoRow, Modal, PacienteRow, Slot, fieldCls,
  DIAS_CORTOS, TIPO_ATENCION, addDays, addMonths, fmtFecha, hhmm, isoDate, parseISO, startOfWeek, medRpc,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

type Vista = 'dia' | 'semana' | 'mes';

const hoyISO = () => isoDate(new Date());

export function Agenda() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<Vista>('dia');
  const [fecha, setFecha] = useState<string>(hoyISO());
  const [medicoId, setMedicoId] = useState<string>('');

  const { data: medicos = [] } = useQuery<MedicoRow[]>({
    queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }),
  });
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({
    queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }),
  });
  // Si la sesión es de un médico, la agenda se fija a él mismo (no elige). Admin/recepción ve el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  useEffect(() => { if (miMedicoId) setMedicoId(miMedicoId); }, [miMedicoId]);

  // rango visible según la vista
  const [desde, hasta] = useMemo<[string, string]>(() => {
    if (vista === 'dia') return [fecha, fecha];
    if (vista === 'semana') { const s = startOfWeek(fecha); return [s, addDays(s, 6)]; }
    const d = parseISO(fecha); const first = isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
    const last = isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    return [startOfWeek(first), addDays(startOfWeek(last), 6)];
  }, [vista, fecha]);

  const { data: citas = [], isFetching } = useQuery<Cita[]>({
    queryKey: ['med-agenda', desde, hasta, medicoId],
    queryFn: () => medRpc<Cita[]>('med_agenda', { _desde: desde, _hasta: hasta, _medico_id: medicoId || null }),
  });

  const [crear, setCrear] = useState<{ fecha: string; hora?: string } | null>(null);
  const [detalle, setDetalle] = useState<Cita | null>(null);

  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['med-agenda'] }); void qc.invalidateQueries({ queryKey: ['med-disp'] }); };

  const navega = (dir: number) => {
    if (vista === 'dia') setFecha(addDays(fecha, dir));
    else if (vista === 'semana') setFecha(addDays(fecha, dir * 7));
    else setFecha(addMonths(fecha, dir));
  };

  const etiquetaRango = vista === 'dia' ? fmtFecha(fecha)
    : vista === 'semana' ? `${parseISO(desde).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })} – ${parseISO(hasta).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : parseISO(fecha).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarDays className="w-5 h-5" /> Agenda</h1>
        <div className="flex items-center gap-2">
          {miMedicoId ? (
            <input className={`${fieldCls} w-56`} value={miMedico?.razon_social ?? ''} readOnly disabled title="Tu agenda como médico" />
          ) : (
            <select className={`${fieldCls} w-56`} value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">Todos los médicos</option>
              {medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
            </select>
          )}
          <Button onClick={() => setCrear({ fecha })}><Plus className="w-4 h-4 mr-1" /> Nueva cita</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border overflow-hidden">
          {(['dia', 'semana', 'mes'] as Vista[]).map((v) => (
            <button key={v} onClick={() => setVista(v)} aria-pressed={vista === v}
              className={`px-3 py-1.5 text-sm capitalize ${vista === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navega(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setFecha(hoyISO())}>Hoy</Button>
          <Button variant="outline" size="sm" onClick={() => navega(1)}><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm font-medium capitalize min-w-[12rem] text-center">{etiquetaRango}</span>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          {isFetching && <div className="py-2 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
          {vista === 'dia' && <VistaDia fecha={fecha} medicoId={medicoId} citas={citas} onSlot={(h) => setCrear({ fecha, hora: h })} onCita={setDetalle} />}
          {vista === 'semana' && <VistaSemana desde={desde} citas={citas} onDia={(d) => { setFecha(d); setVista('dia'); }} onNueva={(d) => setCrear({ fecha: d })} onCita={setDetalle} />}
          {vista === 'mes' && <VistaMes fecha={fecha} desde={desde} hasta={hasta} citas={citas} onDia={(d) => { setFecha(d); setVista('dia'); }} />}
        </CardContent>
      </Card>

      {crear && (
        <CrearCita base={crear} medicoIdSel={medicoId} medicos={medicos} pacientes={pacientes}
          onClose={() => setCrear(null)} onSaved={() => { setCrear(null); refrescar(); }} />
      )}
      {detalle && (
        <DetalleCita cita={detalle} medicos={medicos} onClose={() => setDetalle(null)} onChanged={() => { setDetalle(null); refrescar(); }} />
      )}
    </div>
  );
}

// ---- Vista Día ----
function VistaDia({ fecha, medicoId, citas, onSlot, onCita }: {
  fecha: string; medicoId: string; citas: Cita[]; onSlot: (hora: string) => void; onCita: (c: Cita) => void;
}) {
  const { data: slots = [] } = useQuery<Slot[]>({
    queryKey: ['med-disp', medicoId, fecha],
    queryFn: () => medRpc<Slot[]>('med_disponibilidad', { _medico_id: medicoId, _fecha: fecha }),
    enabled: !!medicoId,
  });

  if (!medicoId) {
    return citas.length === 0
      ? <Vacio texto="Selecciona un médico para ver su disponibilidad, o crea una cita." />
      : <ListaCitas citas={citas} onCita={onCita} mostrarMedico />;
  }

  const citaDeSlot = (s: Slot) => citas.find((c) => c.hora_inicio < s.hora_fin + ':00' && c.hora_fin > s.hora_inicio + ':00');

  return (
    <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
      {slots.length === 0 && <Vacio texto="Este médico no tiene horario configurado para este día." />}
      {slots.map((s) => {
        const c = citaDeSlot(s);
        if (c) return <CitaChip key={s.hora_inicio} cita={c} onClick={() => onCita(c)} />;
        if (!s.disponible) return <div key={s.hora_inicio} className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">{s.hora_inicio} · No disponible</div>;
        return (
          <button key={s.hora_inicio} onClick={() => onSlot(s.hora_inicio)}
            className="flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-muted/60 hover:border-primary transition-colors">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground" /> {s.hora_inicio}–{s.hora_fin}</span>
            <Plus className="w-3.5 h-3.5 text-primary" />
          </button>
        );
      })}
    </div>
  );
}

// ---- Vista Semana ----
function VistaSemana({ desde, citas, onDia, onNueva, onCita }: {
  desde: string; citas: Cita[]; onDia: (d: string) => void; onNueva: (d: string) => void; onCita: (c: Cita) => void;
}) {
  const dias = Array.from({ length: 7 }, (_, i) => addDays(desde, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {dias.map((d, i) => {
        const delDia = citas.filter((c) => c.fecha === d);
        const esHoy = d === hoyISO();
        return (
          <div key={d} className="rounded-md border min-h-[8rem] flex flex-col">
            <button onClick={() => onDia(d)} className={`flex items-center justify-between px-2 py-1 text-xs border-b hover:bg-muted ${esHoy ? 'bg-primary/10 font-semibold' : ''}`}>
              <span>{DIAS_CORTOS[i]} {parseISO(d).getDate()}</span>
              <Plus className="w-3 h-3 text-primary" onClick={(e) => { e.stopPropagation(); onNueva(d); }} />
            </button>
            <div className="flex-1 space-y-1 p-1 overflow-y-auto">
              {delDia.map((c) => <CitaChip key={c.id} cita={c} compact onClick={() => onCita(c)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Vista Mes ----
function VistaMes({ fecha, desde, hasta, citas, onDia }: {
  fecha: string; desde: string; hasta: string; citas: Cita[]; onDia: (d: string) => void;
}) {
  const mesActual = parseISO(fecha).getMonth();
  const dias: string[] = [];
  for (let d = desde; d <= hasta; d = addDays(d, 1)) dias.push(d);
  const porDia = useMemo(() => {
    const m: Record<string, number> = {};
    citas.forEach((c) => { m[c.fecha] = (m[c.fecha] || 0) + 1; });
    return m;
  }, [citas]);

  return (
    <div className="grid grid-cols-7 gap-1">
      {DIAS_CORTOS.map((d) => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>)}
      {dias.map((d) => {
        const n = porDia[d] || 0; const dt = parseISO(d);
        const fuera = dt.getMonth() !== mesActual; const esHoy = d === hoyISO();
        return (
          <button key={d} onClick={() => onDia(d)}
            className={`min-h-[4.5rem] rounded-md border p-1 text-left hover:border-primary transition-colors ${fuera ? 'opacity-40' : ''} ${esHoy ? 'border-primary' : ''}`}>
            <div className="text-xs font-medium">{dt.getDate()}</div>
            {n > 0 && <div className="mt-1 inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">{n} cita{n > 1 ? 's' : ''}</div>}
          </button>
        );
      })}
    </div>
  );
}

// ---- Componentes auxiliares ----
function Vacio({ texto }: { texto: string }) {
  return <div className="py-10 text-center text-sm text-muted-foreground">{texto}</div>;
}

function CitaChip({ cita, onClick, compact }: { cita: Cita; onClick: () => void; compact?: boolean }) {
  const e = ESTADO_CITA[cita.estado] ?? ESTADO_CITA.programada;
  return (
    <button onClick={onClick} className={`w-full text-left rounded-md border px-2 py-1.5 text-sm hover:shadow-sm transition-shadow ${e.cls}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-medium truncate">{hhmm(cita.hora_inicio)} {cita.paciente}</span>
      </div>
      {!compact && <div className="text-xs opacity-80 truncate">{cita.medico} · {cita.motivo || cita.tipo_atencion}</div>}
    </button>
  );
}

function ListaCitas({ citas, onCita, mostrarMedico }: { citas: Cita[]; onCita: (c: Cita) => void; mostrarMedico?: boolean }) {
  return (
    <div className="space-y-1.5">
      {citas.map((c) => {
        const e = ESTADO_CITA[c.estado] ?? ESTADO_CITA.programada;
        return (
          <button key={c.id} onClick={() => onCita(c)} className="w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-muted/50">
            <span className="flex items-center gap-3">
              <span className="font-medium">{hhmm(c.hora_inicio)}–{hhmm(c.hora_fin)}</span>
              <span>{c.paciente}</span>
              {mostrarMedico && <span className="text-muted-foreground">· {c.medico}</span>}
              {c.motivo && <span className="text-muted-foreground">· {c.motivo}</span>}
            </span>
            <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Modal: crear cita ----
// Buscador de pacientes con búsqueda automática al escribir (como los productos del POS):
// varias palabras en cualquier orden, sin importar tildes, por nombre, identificación,
// código o teléfono. Flechas ↑/↓ para moverse, Enter para elegir, Esc para cerrar.
const sinTildesPac = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
function BuscadorPaciente({ pacientes, value, onChange }: {
  pacientes: PacienteRow[]; value: string; onChange: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [idx, setIdx] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const elegido = pacientes.find((p) => p.id === value) ?? null;
  const indice = useMemo(() => pacientes.map((p) => ({
    p, texto: sinTildesPac([p.razon_social, p.identificacion, p.codigo, p.telefono, p.celular].filter(Boolean).join(' ')),
  })), [pacientes]);
  const resultados = useMemo(() => {
    const palabras = sinTildesPac(q).split(/\s+/).filter(Boolean);
    if (palabras.length === 0) return [];
    return indice.filter((x) => palabras.every((w) => x.texto.includes(w))).slice(0, 30).map((x) => x.p);
  }, [indice, q]);
  useEffect(() => { setIdx(0); }, [q]);
  const elegir = (p: PacienteRow) => { onChange(p.id); setQ(''); setAbierto(false); };

  if (elegido) {
    return (
      <div className={`${fieldCls} flex items-center justify-between gap-2`}>
        <span className="truncate"><span className="font-medium">{elegido.razon_social}</span>
          <span className="text-muted-foreground"> · {elegido.identificacion ?? 's/i'}</span></span>
        <button type="button" className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
          onClick={() => { onChange(''); setTimeout(() => ref.current?.focus(), 0); }}>
          <X className="h-3.5 w-3.5" /> Cambiar</button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input ref={ref} autoFocus className={`${fieldCls} pl-8`} value={q} placeholder="Escriba nombre, cédula o teléfono…"
        aria-label="Buscar paciente" autoComplete="off"
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, resultados.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter' && resultados[idx]) { e.preventDefault(); elegir(resultados[idx]); }
          else if (e.key === 'Escape') { setAbierto(false); }
        }} />
      {abierto && q.trim() !== '' && (
        <ul className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-background shadow-lg" role="listbox">
          {resultados.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Ningún paciente coincide con «{q}».</li>
          ) : resultados.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === idx}
              onMouseDown={(e) => { e.preventDefault(); elegir(p); }} onMouseEnter={() => setIdx(i)}
              className={`cursor-pointer px-3 py-1.5 text-sm ${i === idx ? 'bg-primary/10' : ''}`}>
              <p className="font-medium">{p.razon_social}</p>
              <p className="text-xs text-muted-foreground">{p.identificacion ?? 's/i'}{p.celular || p.telefono ? ` · ${p.celular ?? p.telefono}` : ''}{p.aseguradora ? ` · ${p.aseguradora}` : ''}</p>
            </li>
          ))}
          {resultados.length === 30 && <li className="px-3 py-1.5 text-xs text-muted-foreground">Se muestran los primeros 30: siga escribiendo para afinar.</li>}
        </ul>
      )}
    </div>
  );
}

function CrearCita({ base, medicoIdSel, medicos, pacientes, onClose, onSaved }: {
  base: { fecha: string; hora?: string }; medicoIdSel: string; medicos: MedicoRow[]; pacientes: PacienteRow[];
  onClose: () => void; onSaved: () => void;
}) {
  const [pacienteId, setPacienteId] = useState('');
  const [medicoId, setMedicoId] = useState(medicoIdSel || '');
  // Si la sesión es de un médico, la cita se crea a su nombre (no elige).
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  useEffect(() => { if (miMedicoId) setMedicoId(miMedicoId); }, [miMedicoId]);
  const [fecha, setFecha] = useState(base.fecha);
  const [hora, setHora] = useState(base.hora ?? '');
  const [tipo, setTipo] = useState('presencial');
  const [motivo, setMotivo] = useState('');

  const { data: slots = [] } = useQuery<Slot[]>({
    queryKey: ['med-disp', medicoId, fecha],
    queryFn: () => medRpc<Slot[]>('med_disponibilidad', { _medico_id: medicoId, _fecha: fecha }),
    enabled: !!medicoId && !!fecha,
  });
  const libres = slots.filter((s) => s.disponible);

  // Coherencia por sexo (691): al elegir paciente, se ocultan los médicos de
  // especialidades incompatibles (p. ej. Ginecología para paciente masculino).
  const generoPac = pacientes.find((p) => p.id === pacienteId)?.genero ?? null;
  const medicosVisibles = medicos.filter((m) =>
    !m.especialidad_sexo || !generoPac || (generoPac !== 'F' && generoPac !== 'M') || m.especialidad_sexo === generoPac);
  useEffect(() => {
    if (medicoId && !miMedicoId && !medicosVisibles.some((m) => m.id === medicoId)) setMedicoId('');
  }, [generoPac]);   // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = useMutation({
    mutationFn: () => medRpc('med_crear_cita', { _data: { paciente_id: pacienteId, medico_id: medicoId, fecha, hora_inicio: hora, tipo_atencion: tipo, motivo: motivo || null } }),
    onSuccess: () => { toast.success('Cita creada'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Nueva cita" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Paciente *">
          <BuscadorPaciente pacientes={pacientes} value={pacienteId} onChange={setPacienteId} />
        </Campo>
        <Campo label="Médico *">
          {miMedicoId ? (
            <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
          ) : (
            <select className={fieldCls} value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {medicosVisibles.map((m) => <option key={m.id} value={m.id}>{m.razon_social}{m.especialidad ? ` · ${m.especialidad}` : ''}</option>)}
            </select>
          )}
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Hora *">
            {medicoId && libres.length > 0 ? (
              <select className={fieldCls} value={hora} onChange={(e) => setHora(e.target.value)}>
                <option value="">— Cupo —</option>
                {libres.map((s) => <option key={s.hora_inicio} value={s.hora_inicio}>{s.hora_inicio}–{s.hora_fin}</option>)}
              </select>
            ) : (
              <input type="time" className={fieldCls} value={hora} onChange={(e) => setHora(e.target.value)} />
            )}
          </Campo>
        </div>
        {medicoId && fecha && libres.length === 0 && <p className="text-xs text-pendiente">No hay cupos libres según el horario; puedes fijar una hora manual.</p>}
        <Campo label="Tipo de atención">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPO_ATENCION.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Campo>
        <Campo label="Motivo"><AutoTextarea value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!pacienteId || !medicoId || !fecha || !hora} onClick={() => guardar.mutate()}>Crear cita</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Modal: detalle / acciones de cita ----
function DetalleCita({ cita, medicos, onClose, onChanged }: {
  cita: Cita; medicos: MedicoRow[]; onClose: () => void; onChanged: () => void;
}) {
  const dlg = useDialogoEnLinea();
  const [reagendar, setReagendar] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState(cita.fecha);
  const [nuevaHora, setNuevaHora] = useState(hhmm(cita.hora_inicio));
  const [nuevoMedico, setNuevoMedico] = useState(cita.medico_id);
  // Si la sesión es de un médico, el médico de la cita se fija a él mismo (no elige).
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  useEffect(() => { if (miMedicoId) setNuevoMedico(miMedicoId); }, [miMedicoId]);
  const e = ESTADO_CITA[cita.estado] ?? ESTADO_CITA.programada;

  const accion = useMutation({
    mutationFn: ({ fn, body }: { fn: string; body: Record<string, unknown> }) => medRpc(fn, body),
    onSuccess: () => { toast.success('Cita actualizada'); onChanged(); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const cambiarEstado = (estado: string) => accion.mutate({ fn: 'med_cambiar_estado_cita', body: { _id: cita.id, _estado: estado } });
  const activa = !['cancelada', 'atendida', 'no_asistio'].includes(cita.estado);

  return (
    <Modal title="Detalle de la cita" onClose={onClose}>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold">{cita.paciente}</span>
          <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          <Dato k="Médico" v={cita.medico} />
          <Dato k="Especialidad" v={cita.especialidad ?? '—'} />
          <Dato k="Fecha" v={cita.fecha} />
          <Dato k="Hora" v={`${hhmm(cita.hora_inicio)}–${hhmm(cita.hora_fin)}`} />
          <Dato k="Tipo" v={cita.tipo_atencion} />
          <Dato k="Consultorio" v={cita.consultorio ?? '—'} />
        </dl>
        {cita.motivo && <p><span className="text-muted-foreground">Motivo: </span>{cita.motivo}</p>}

        {!reagendar ? (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            {activa && cita.estado === 'programada' && <Button size="sm" variant="outline" onClick={() => cambiarEstado('confirmada')}>Confirmar</Button>}
            {activa && ['programada', 'confirmada'].includes(cita.estado) && <Button size="sm" variant="outline" onClick={() => accion.mutate({ fn: 'med_checkin_cita', body: { _id: cita.id } })}>Check-in</Button>}
            {activa && cita.estado === 'en_espera' && <Button size="sm" variant="outline" onClick={() => cambiarEstado('en_atencion')}>En atención</Button>}
            {activa && ['en_espera', 'en_atencion', 'confirmada'].includes(cita.estado) && <Button size="sm" variant="outline" onClick={() => cambiarEstado('atendida')}>Atendida</Button>}
            {activa && <Button size="sm" variant="outline" onClick={() => setReagendar(true)}>Reagendar</Button>}
            {activa && <Button size="sm" variant="outline" onClick={() => cambiarEstado('no_asistio')}>No asistió</Button>}
            {activa && <Button size="sm" variant="destructive" onClick={async () => {
              const v = await dlg.pedir('Cancelar cita', [{ key: 'motivo', label: 'Motivo de la cancelación', minimo: 0 }]);
              if (v === null) return;
              accion.mutate({ fn: 'med_cancelar_cita', body: { _id: cita.id, _motivo: v.motivo || undefined } });
            }}>Cancelar cita</Button>}
          </div>
        ) : (
          <div className="border-t pt-3 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <Campo label="Médico">{miMedicoId ? (
                <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
              ) : (
                <select className={fieldCls} value={nuevoMedico} onChange={(ev) => setNuevoMedico(ev.target.value)}>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}</select>
              )}</Campo>
              <Campo label="Fecha"><input type="date" className={fieldCls} value={nuevaFecha} onChange={(ev) => setNuevaFecha(ev.target.value)} /></Campo>
              <Campo label="Hora"><input type="time" className={fieldCls} value={nuevaHora} onChange={(ev) => setNuevaHora(ev.target.value)} /></Campo>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setReagendar(false)}>Volver</Button>
              <Button size="sm" isLoading={accion.isPending} onClick={() => accion.mutate({ fn: 'med_reagendar_cita', body: { _id: cita.id, _fecha: nuevaFecha, _hora_inicio: nuevaHora, _medico_id: nuevoMedico } })}>Guardar cambio</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Dato({ k, v }: { k: string; v: string }) {
  return <><dt className="text-muted-foreground">{k}</dt><dd className="text-right capitalize">{v}</dd></>;
}
